import React from 'react';
import { Target, Clock, TrendingUp } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { eventsData, categoryColors } from '../../../data/eventsData';

const getImpactStyle = (impact: string): string => {
  const styles: Record<string, string> = {
    'CRITICAL': 'bg-data-red text-white font-bold',
    'HIGH': 'bg-data-green text-black font-bold',
    'MEDIUM': 'bg-data-amber text-black font-semibold',
    'LOW': 'bg-gray-600 text-white',
  };
  return styles[impact] || 'bg-gray-600 text-white';
};

const getUrgencyColor = (days: number): string => {
  if (days < 30) return 'text-data-red';
  if (days < 90) return 'text-data-amber';
  return 'text-data-green';
};

interface CatalystCountdownPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const CatalystCountdownPanel: React.FC<CatalystCountdownPanelProps> = ({ isMinimized, onMinimize }) => {
  const { catalysts, summary } = eventsData;
  const mainCatalyst = catalysts[0];
  const secondaryCatalysts = catalysts.slice(1, 5);

  const avgPricedIn = catalysts.reduce((sum, c) => sum + c.priceIn, 0) / catalysts.length;
  const highImpactCount = catalysts.filter(c => c.impact === 'HIGH' || c.impact === 'CRITICAL').length;

  return (
    <Panel title="CATALYST COUNTDOWN" subtitle="MAJOR EVENTS" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero - Next Major Catalyst */}
        <div className="text-center py-5 px-4 bg-gradient-to-b from-data-amber/10 to-transparent rounded-lg border border-data-amber/20 mb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Target size={16} className="text-data-amber" />
            <span className="text-xs text-data-amber uppercase tracking-widest font-semibold">
              Next Catalyst
            </span>
          </div>
          <div className={`text-5xl font-mono font-black mb-2 ${getUrgencyColor(mainCatalyst.daysUntil)}`}>
            {mainCatalyst.daysUntil}<span className="text-2xl">D</span>
          </div>
          <div className="text-sm font-semibold text-white mb-1">{mainCatalyst.event}</div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className={`text-xs px-2.5 py-1 rounded ${getImpactStyle(mainCatalyst.impact)}`}>
              {mainCatalyst.impact}
            </span>
            <span className="text-xs text-gray-400">
              <span className="text-data-green font-bold">{mainCatalyst.probability}%</span> probability
            </span>
          </div>
        </div>

        {/* Key Stats Row */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-green">{summary.upcomingCatalysts}</div>
            <div className="text-xs text-gray-500">Events Tracked</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-amber">{highImpactCount}</div>
            <div className="text-xs text-gray-500">High Impact</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-white">{avgPricedIn.toFixed(0)}%</div>
            <div className="text-xs text-gray-500">Avg Priced In</div>
          </div>
        </div>

        {/* Upcoming Catalysts List */}
        <div className="flex-1 space-y-2 overflow-auto custom-scrollbar">
          {secondaryCatalysts.map((catalyst) => (
            <div key={catalyst.id} className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-white">{catalyst.event}</span>
                <span className={`text-lg font-mono font-black ${getUrgencyColor(catalyst.daysUntil)}`}>
                  {catalyst.daysUntil}d
                </span>
              </div>

              {/* Progress Bar */}
              <div className="h-2 bg-terminal-border rounded-full overflow-hidden mb-2">
                <div
                  className="h-full bg-data-green rounded-full"
                  style={{ width: `${catalyst.priceIn}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${getImpactStyle(catalyst.impact)}`}>
                    {catalyst.impact}
                  </span>
                  <span style={{ color: categoryColors[catalyst.category] }} className="text-xs">
                    {catalyst.category}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-gray-500">
                    <span className="text-data-green font-bold">{catalyst.probability}%</span> prob
                  </span>
                  <span className="text-gray-500">
                    <span className="text-data-amber font-bold">{catalyst.priceIn}%</span> priced
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default CatalystCountdownPanel;
